// ✅ Remover overlay vermelho causado por "ResizeObserver loop..." em Webpack Dev Server
const RESIZE_OBSERVER_ERROR = 'ResizeObserver loop completed with undelivered notifications.';

// 1. Intercetar o erro antes que chegue ao overlay
window.addEventListener('error', (event) => {
  if (event?.message?.includes(RESIZE_OBSERVER_ERROR)) {
    event.stopImmediatePropagation();
    event.preventDefault();
  }
});

// 2. Monitorizar o DOM e remover o overlay assim que for adicionado
setTimeout(() => {
  const observer = new MutationObserver(() => {
    const overlay = document.getElementById('webpack-dev-server-client-overlay-div');
    const backdrop = document.getElementById('webpack-dev-server-client-overlay');

    if (overlay?.innerText?.includes(RESIZE_OBSERVER_ERROR)) {
      overlay.remove();
      backdrop?.remove();
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });
}, 100);

// adding event listener to prevent overlapping redirects / errors
window.addEventListener("unhandledrejection", (event) => {
  const statusCode = event?.reason?.response?.status ?? null;
  const isHandledSessionExpiry = event?.reason?.__sessionExpiredHandled === true;

  if (isHandledSessionExpiry || statusCode === 401 || statusCode === 419) {
    event.preventDefault();
  }
});

// src/main.js
import { createApp } from 'vue'
import App from './App.vue'
import vuetify from './plugins/vuetify'
import './plugins/validations'
import VueGoogleMaps from '@fawmi/vue-google-maps'
import 'roboto-fontface/css/roboto/roboto-fontface.css'
import '@mdi/font/css/materialdesignicons.css'
import "./assets/scss/main.scss"

import axios from 'axios'
import router from './router'
import dayjs from 'dayjs'

import VueCookies from 'vue-cookies'
import EncryptValue from '@/plugins/encrypt'

import { createPinia } from 'pinia'
import { useSessionStore } from '@/stores/SessionStore'

import VueApexCharts from 'vue3-apexcharts'
import keycloak from './keycloak.js'

// =========================================================
// OLYMPUS
// =========================================================

const OLYMPUS_URL = (process.env.VUE_APP_LOGIN_PORTAL_URL || '').replace(/\/+$/, '')

const redirectToOlympus = () => {
  if (!OLYMPUS_URL) {
    console.error('VUE_APP_LOGIN_PORTAL_URL não está configurado.')
    return
  }

  window.location.href = OLYMPUS_URL
}

// =========================================================
// RESIZE OBSERVER
// =========================================================

const observerError = 'ResizeObserver loop completed with undelivered notifications.'

const originalRO = window.ResizeObserver

window.ResizeObserver = class ResizeObserver extends originalRO {
  constructor(callback) {
    const patchedCallback = function(entries, observer) {
      try {
        callback(entries, observer)
      } catch (e) {
        if (
          e instanceof Error &&
          e.message === observerError
        ) {
          return
        }

        throw e
      }
    }

    super(patchedCallback)
  }
}

// SUPRIMIR APENAS "ResizeObserver loop" do overlay do webpack
window.addEventListener('error', (event) => {
  if (
    event.message &&
    event.message.includes(
      'ResizeObserver loop completed with undelivered notifications'
    )
  ) {
    event.stopImmediatePropagation()
  }
})

// =========================================================
// APP
// =========================================================

const app = createApp(App)
const pinia = createPinia()

app.use(pinia)

// Cookies
app.use(VueCookies, {
  expires: '7d',
})

// =========================================================
// GOOGLE MAPS
// =========================================================

app.use(VueGoogleMaps, {
  load: {
    key: process.env.VUE_APP_GOOGLE_MAPS_KEY,
    libraries: 'places',
    v: 'weekly',
  },
})

// =========================================================
// AXIOS
// =========================================================

axios.interceptors.request.use(
  (config) => {
    config.withCredentials = true
    config.headers['Content-Type'] = 'application/json'
    config.headers['Accept'] = 'application/json'

    return config
  },
  (error) => Promise.reject(error)
)

axios.interceptors.response.use(
  (response) => response,

  (error) => {
    const status = error?.response?.status

    if (status === 401 || status === 419) {
      /*
       * A sessão do Nexus terminou.
       *
       * Não fazemos keycloak.login() nem router.push('/').
       * Mandamos o utilizador de volta para o Olympus.
       */
      error.__sessionExpiredHandled = true

      redirectToOlympus()
    }

    return Promise.reject(error)
  }
)

// =========================================================
// GLOBAIS
// =========================================================

app.config.globalProperties.$fatalError = true

app.config.errorHandler = (error, instance, info) => {
  window.alert(
    `Vue error: ${error?.message ?? error}\n${info}`
  )

  console.error(
    error,
    instance,
    info
  )
}

// =========================================================
// THEME
// =========================================================

const scssThemeColorFallbacks = {
  // primary: "#1e3582",
  primary: "#03050d",
  success: "#1DB954",
  info: "#2196F3",
  warning: "#FFAA33",
  error: "#990000",
  muted: "#a0a0a0",
  light: "#ffffff",
  dark: "#03050d",
  background: "#e8eaf2",
}

const getScssThemeColors = () => {
  const rootStyles = getComputedStyle(
    document.documentElement
  )

  return Object.entries(
    scssThemeColorFallbacks
  ).reduce(
    (colors, [name, fallback]) => {
      const color = rootStyles
        .getPropertyValue(`--app-color-${name}`)
        .trim()

      colors[name] = color || fallback

      return colors
    },
    {}
  )
}

const createThemeConfig = (colors) => ({
  name: "Padrão",

  light: {
    primary: colors.primary,
    primaryLight: colors.primary,
    secondary: colors.muted,
    accent: colors.info,
    error: colors.error,
    warning: colors.warning,
    info: colors.info,
    success: colors.success,
    anchor: colors.primary,
    muted: colors.muted,
    light: colors.light,
    dark: colors.dark,
    background: colors.background,
    surface: colors.light,
  },

  dark: {
    primary: colors.primary,
    primaryLight: colors.primary,
    secondary: colors.muted,
    accent: colors.info,
    error: colors.error,
    warning: colors.warning,
    info: colors.info,
    success: colors.success,
    anchor: colors.primary,
    muted: colors.muted,
    light: colors.light,
    dark: colors.dark,
    background: colors.dark,
    surface: colors.dark,
  },
})

const setVuetifyThemeColors = (
  vuetifyInstance,
  themeConfig
) => {
  const vuetifyThemes =
    vuetifyInstance?.theme?.themes?.value

  if (!vuetifyThemes) {
    return
  }

  vuetifyThemes.light.colors = {
    ...vuetifyThemes.light.colors,
    ...themeConfig.light,
  }

  vuetifyThemes.dark.colors = {
    ...vuetifyThemes.dark.colors,
    ...themeConfig.dark,
  }
}

const themes = [
  createThemeConfig(
    getScssThemeColors()
  ),
]

setVuetifyThemeColors(
  vuetify,
  themes[0]
)

app.config.globalProperties.$themes = themes
app.config.globalProperties.$encryptValue = EncryptValue

app.config.globalProperties.$session =
  useSessionStore()

// ⚠️ Guarda para setups que ainda expõem this.$vuetify
app.mixin({
  methods: {
    setTheme(theme) {
      const dark = theme.dark
      const light = theme.light

      if (this.$vuetify?.theme?.themes) {
        Object.keys(dark).forEach((i) => {
          this.$vuetify.theme.themes.dark[i] =
            dark[i]
        })

        Object.keys(light).forEach((i) => {
          this.$vuetify.theme.themes.light[i] =
            light[i]
        })

        this.$vuetify.groupSelectedColor =
          this.$vuetify.theme.dark
            ? theme.dark.primaryLight
            : theme.light.primaryLight
      }
    },

    formatDate(date) {
      return date == null
        ? null
        : dayjs(date).format('DD/MM/YYYY')
    },
  },
})

// Charts Vue 3
app.use(VueApexCharts)

// =========================================================
// KEYCLOAK
// =========================================================

keycloak.init({
  /*
   * IMPORTANTE:
   *
   * Antes:
   *   onLoad: 'login-required'
   *
   * Isso fazia o Nexus enviar automaticamente o utilizador
   * para a página de login do Keycloak.
   *
   * Agora apenas verificamos se existe sessão.
   */
  onLoad: 'check-sso',

  checkLoginIframe: false,
})
.then((authenticated) => {

  /*
   * Não existe sessão Keycloak válida.
   *
   * O Nexus não inicia um login.
   * Volta para o Olympus.
   */
  if (!authenticated) {
    redirectToOlympus()
    return
  }

  // =======================================================
  // TOKEN EXPIRADO
  // =======================================================

  keycloak.onTokenExpired = async () => {
    try {
      /*
       * Tentamos renovar o token.
       */
      const refreshed = await keycloak.updateToken(30)

      if (!refreshed) {
        /*
         * O token ainda pode estar válido.
         * Não fazemos nada.
         */
        return
      }
    } catch (error) {
      console.error(
        'Não foi possível renovar a sessão do Keycloak.',
        error
      )

      /*
       * Refresh token também expirou / sessão terminou.
       */
      redirectToOlympus()
    }
  }

  // =======================================================
  // MONTAR APP
  // =======================================================

  app.use(vuetify)
  app.use(router)

  app.mount('#app')

  router.isReady().then(() => {
    if (
      ["/", "/login"].includes(
        router.currentRoute.value.path
      )
    ) {
      router
        .replace("/admin")
        .catch(() => {})
    }
  })
})
.catch((err) => {
  console.error(
    'Erro ao iniciar Keycloak',
    err
  )

  /*
   * Em vez de mandar para o Keycloak,
   * voltamos para o Olympus.
   */
  redirectToOlympus()
})

// =========================================================
// CONSOLE ERROR
// =========================================================

console.error = ((error) => {
  return (...args) => {
    if (
      typeof args[0] === 'string' &&
      args[0].includes(
        'ResizeObserver loop completed'
      )
    ) {
      return
    }

    error(...args)
  }
})(console.error)